import { useCallback, useEffect, useId, useRef } from 'react';
import { useDebounce } from '@mentimeter/react-hooks';
import { resizeText } from './resizeText';

type TextFitGhostStyles = Pick<React.HTMLAttributes<HTMLDivElement>, 'style'>;
export interface TextFitGhostProps extends TextFitGhostStyles {
  text: string;
  width: number | string;
  height: number | string;
  fontSizes: string[];
  children: (ref: React.RefObject<HTMLSpanElement>) => React.ReactNode;
  finalFontSizeContainerRef?: React.RefObject<HTMLDivElement>;
  isWaitingForFontToLoad?: boolean;
  shouldResizeFontSizeContainerWhileIterating?: boolean;
}

/**
 * Renders an invisible component that resizes a child text element based on the width and height of the parent container
 * and sets the final font size on a container ref
 */
export const TextFitGhost = ({
  children,
  width,
  height,
  text,
  fontSizes,
  finalFontSizeContainerRef,
  style,
  isWaitingForFontToLoad,
  shouldResizeFontSizeContainerWhileIterating = false,
}: TextFitGhostProps) => {
  const textValueRef = useRef<string>(text);
  const textRef = useRef<HTMLSpanElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const resizeTimerId = useId();

  // we cannot use scrollHeight because then we cannot detect if the text is too small
  const containerHeight =
    containerRef.current?.getBoundingClientRect().height ?? 0;

  const handleUpdateFontSize = useCallback(
    (fontSize: string) => {
      if (textRef.current) {
        textRef.current.style.fontSize = fontSize;
      }
      if (
        shouldResizeFontSizeContainerWhileIterating &&
        finalFontSizeContainerRef?.current
      ) {
        finalFontSizeContainerRef.current.style.fontSize = fontSize;
      }
    },
    [
      textRef,
      finalFontSizeContainerRef,
      shouldResizeFontSizeContainerWhileIterating,
    ],
  );

  const handleFinalFontSizeUpdate = useCallback(
    (fontSize: string) => {
      if (finalFontSizeContainerRef?.current) {
        finalFontSizeContainerRef.current.style.fontSize = fontSize;
        finalFontSizeContainerRef.current.style.opacity = '1';
      }
    },
    [finalFontSizeContainerRef],
  );

  const resizeTextCallback = useCallback(() => {
    if (isWaitingForFontToLoad) {
      return;
    }
    textValueRef.current = text;
    resizeText({
      id: resizeTimerId,
      containerHeight: // make sure we have the latest container height
        containerRef.current?.getBoundingClientRect().height ?? 0,
      textRef,
      fontSizes,
      textValueRef,
      textValueOfRecursionRoot: text,
      onUpdateFontSize: handleUpdateFontSize,
      onFinalFontSizeUpdate: handleFinalFontSizeUpdate,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we need to listen to changes in width, height and containerHeight
  }, [
    containerRef,
    text,
    fontSizes,
    finalFontSizeContainerRef,
    width,
    height,
    containerHeight,
    isWaitingForFontToLoad,
    shouldResizeFontSizeContainerWhileIterating,
  ]);

  useDebounce(resizeTextCallback, 20);

  useEffect(() => {
    const fallbackTimerId = setTimeout(() => {
      const containerIsHidden =
        finalFontSizeContainerRef?.current?.style.opacity === '0';
      if (finalFontSizeContainerRef?.current && containerIsHidden) {
        const fallbackFontSize = fontSizes[0];
        if (fallbackFontSize) {
          finalFontSizeContainerRef.current.style.fontSize = fallbackFontSize;
        }
        finalFontSizeContainerRef.current.style.opacity = '1';
      }
    }, 2000);
    return () => {
      clearTimeout(fallbackTimerId);
    };
  }, [finalFontSizeContainerRef, fontSizes]);

  return (
    <div
      data-testid="text-fit-wrapper"
      aria-hidden="true"
      ref={containerRef}
      style={{
        width: '100%',
        height,
        position: 'absolute',
        opacity: 0,
        zIndex: -1,
        ...style,
      }}
    >
      {children(textRef)}
    </div>
  );
};
